import { AbyssTheme as themeConfiguration } from '@src/app/browser';
import { useApi } from '@src/app/context/Api';
import { FormModal } from '@src/entities/Initiative/ui/components/FormModal';
import { InitiativesTable } from '@src/entities/Initiative/ui/components/tables/Initiatives';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { motion } from 'framer-motion';
import React, { useState } from 'react';

/**
 * Page: Initiatives
 *
 * Provides the user with a screen listing the existing initiatives.
 *
 * @returns {Element}
 * @constructor
 */
export function Initiatives() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [currentEntity, setCurrentEntity] = useState({});

  const { clearApiCache, useApiQuery } = useApi('Initiative');
  const { useApiMutation } = useApi('Code');

  const [ListInitiativeTypes, { data, error, isFetching, isLoading, refetch }] = useApiQuery('ListInitiativeTypes');
  const [SaveCode] = useApiMutation('SaveCode');

  const { toast } = useToast();

  /**
   * handleSave
   *
   * Handles the saving of a initiative.
   *
   * @param payload
   * @returns {Promise<void>}
   */
  const handleSave = async (payload = {}) => {
    const toastId = 'save-initiative';

    toast.show({
      ariaLoadingLabel: 'Saving Initiative',
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: 'Initiative is preparing to save.',
      title: 'Saving Initiative...',
      variant: 'info',
    });

    await SaveCode(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: `Unable to save initiative.`,
          title: 'Initiative Save Failed',
          variant: 'error',
        });
      },
      onSuccess: async () => {
        await clearApiCache([{ apiKey: 'Initiative', queryKeys: ['ListInitiativeTypes'] }]);
        refetch();
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-success`,
          message: `Initiative has been saved.`,
          title: 'Saved Initiative',
          variant: 'success',
        });
      },
    });
  };

  return (
    <ErrorHandler location="src/pages/private/Admin/Initiatives/Initiatives.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <Flex alignItems="center" direction="row">
              <Heading offset={0}>Initiatives</Heading>
              <User.Capability attributes={['create']} resource="Admin:Initiatives">
                <Button
                  before={<IconSymbol icon="add" />}
                  css={{
                    marginLeft: themeConfiguration?.theme?.space?.md,
                  }}
                  data-testid="create-button"
                  isDisabled={isLoading || isFetching}
                  onClick={() => {
                    setIsFormModalOpen(true);
                    setCurrentEntity({});
                  }}
                  size="$sm"
                  variant="outline"
                >
                  Create
                </Button>
              </User.Capability>
            </Flex>
          </Grid.Col>
          <Grid.Col
            span={{
              xs: '100%',
            }}
          >
            <InitiativesTable
              error={error}
              handleSave={handleSave}
              isLoading={isLoading || isFetching}
              refetch={refetch}
              requestArgs={{
                page: 0,
                size: 25,
                sort: 'codeId,desc',
              }}
              requestFunction={ListInitiativeTypes}
              requestKey="ListInitiativeTypes"
              rows={data || []}
              setCurrentEntity={setCurrentEntity}
              setIsFormModalOpen={setIsFormModalOpen}
              totalPages={data?.totalPages || 1}
              totalRecords={data?.totalElements || 0}
            />

            {isFormModalOpen && (
              <FormModal
                currentEntity={currentEntity}
                handleSave={handleSave}
                isOpen={isFormModalOpen}
                setCurrentEntity={setCurrentEntity}
                setIsOpen={setIsFormModalOpen}
              />
            )}
          </Grid.Col>
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
